import { createFileRoute, Navigate, redirect } from '@tanstack/react-router'
import { ProductGuard } from '@bloo-team/core-shell'
import { isExcludedRoute, legacyRedirectFor, navPath } from '@bloo-team/core-shell'
import { entitledProducts, isProductKey, productById } from '@bloo-team/core-shell'
import { findCluster, getSession, identityFor, useSession } from '../../../lib/session'

/**
 * Scope-level catch-all: legacy URLs first, then the deep-link guard (D4).
 *
 * Being deeper than the cluster catch-all (`$clusterId/$.tsx`), this route
 * takes every /{clusterId}/{x}/{...} path with no screen behind it, so it
 * must answer both readings of {x}:
 *
 *   {x} is a real scope   → the rest is either a legacy path (redirect, same
 *                           table as before), an unknown product ("not part
 *                           of this deployment"), a registered-but-unentitled
 *                           product ("not enabled for this organization"), or
 *                           an entitled product's dead subpath (→ Home);
 *   {x} is NOT a scope    → the whole tail is a legacy cluster-level path
 *                           (/{c}/incident/signals …): redirect into the
 *                           cluster's first scope, exactly as the cluster
 *                           catch-all always did — unknowns land Home.
 *
 * The guard pages render here, INSIDE the shell (this route sits under the
 * $scopeId layout), which is the D4 requirement: a say-so page, not a 404.
 */
export const Route = createFileRoute('/$clusterId/$scopeId/$')({
  beforeLoad: ({ params }) => {
    const { clusterId, scopeId } = params
    const splat = `/${(params as { _splat?: string })._splat ?? ''}`
    const session = getSession()
    const cluster = session ? findCluster(session, clusterId) : undefined
    const scopeReal = cluster?.scopes.some((s) => s.scopeId === scopeId) ?? false
    const fallbackScope = cluster?.scopes[0]?.scopeId ?? scopeId

    // A redirect value may carry a search string ("?tab=suppression").
    const go = (scope: string, target: string): never => {
      const [path = target, qs] = target.split('?')
      throw redirect({
        to: `/${clusterId}/${scope}${path}` as string,
        ...(qs ? { search: Object.fromEntries(new URLSearchParams(qs)) } : {}),
        replace: true,
      })
    }
    // Exact table rows plus the id-carrying prefixes (/workbook/:id,
    // /system/streamfieldaddition/:stream) resolve in one place (core-shell nav.ts).
    if (scopeReal) {
      const legacy = legacyRedirectFor(splat)
      if (legacy) go(scopeId, legacy)
      if (splat === '/') go(scopeId, '/home')
      return // fall through: the component decides between guard pages and Home
    }

    // {scopeId} is itself the head of a legacy path.
    const asLegacy = splat === '/' ? `/${scopeId}` : `/${scopeId}${splat}`
    const legacy = legacyRedirectFor(asLegacy)
    if (legacy) go(fallbackScope, legacy)
    go(fallbackScope, '/home')
  },
  component: ScopeCatchAll,
})

function ScopeCatchAll() {
  const params = Route.useParams() as { clusterId: string; scopeId: string; _splat?: string }
  const splat = `/${params._splat ?? ''}`
  const head = (params._splat ?? '').split('/').filter(Boolean)[0] ?? ''
  const session = useSession()
  if (!session) return null

  if (isProductKey(head)) {
    const entitled = entitledProducts(identityFor(session, params.clusterId, params.scopeId))
    if (!entitled.has(head)) {
      return <ProductGuard kind="unentitled" productName={productById(head)?.name ?? head} />
    }
    // A page this BUILD was composed without (docs/page-gating.md).
    // It reaches the catch-all exactly like a mistyped subpath, but it is not
    // one, and Home would be a confusing answer to a URL that was correct
    // until this deployment was composed. The excluded paths are installed by
    // the generated composer, so this stays profile-ignorant (rule 11); on a
    // full build the set is empty and this branch never fires.
    if (isExcludedRoute(splat)) {
      return <ProductGuard kind="unknown-page" productName={productById(head)?.name ?? head} />
    }
    // Entitled, but nothing at this address inside the product — Home.
    return <Navigate to={navPath(params.clusterId, params.scopeId, '/home')} replace />
  }
  return <ProductGuard kind="unknown" productName={PRODUCT_DISPLAY_NAMES[head] ?? head} />
}

/**
 * Display names for products a build may NOT contain (requirements §4: the
 * guard needs the name even when the product code is absent). Deliberately
 * inline — importing `profiles/products.json` here would embed every
 * product's `bundleMarkers` strings in the app chunk and break
 * `scripts/bundle-scan.mjs`. Pinned against the catalog's `name` fields by
 * `product-registry.test.ts`.
 */
export const PRODUCT_DISPLAY_NAMES: Record<string, string> = {
  fabric: 'Datafabric',
  vantage: 'Vantage',
  synthai: 'SynthAI',
}
